import React, { useId, useMemo, useState } from 'react';
import type {
  ScheduleConfirmPreview,
  ScheduleConfirmRequest,
  SuggestedSchedule,
} from '../../types';

/** Per-card lifecycle, mirroring McpConfigureBuiltinCard but for the schedule gate. */
export type ScheduleConfirmState =
  | 'pending'
  | 'confirming'
  | 'confirmed'
  | 'declined'
  | 'superseded';

interface ScheduleConfirmCardProps {
  preview: ScheduleConfirmPreview;
  state: ScheduleConfirmState;
  /** Confirm: carries the final cadence the user adjusted (cron OR interval). */
  onConfirm: (req: ScheduleConfirmRequest) => void;
  onDecline: () => void;
}

/**
 * Cron presets reused verbatim from ScheduleConfigPanel's vocabulary (daily /
 * weekdays / weekly / monthly / hourly). The concierge card offers the same set
 * minus the free-form "custom" expression — the suggestion is always one of
 * these, and the narrow chat column favours a curated choice over a raw cron box.
 */
type CronPreset = 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'hourly';
type ScheduleType = 'cron' | 'interval';

const CRON_PRESET_OPTIONS: { value: CronPreset; label: string }[] = [
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Weekdays (Mon-Fri)' },
  { value: 'weekly', label: 'Weekly on a specific day' },
  { value: 'monthly', label: 'Monthly on a specific day' },
  { value: 'hourly', label: 'Every hour' },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

/**
 * Build a cron expression from the preset + time + day-of-week. Mirrors
 * `buildCronExpression` in ScheduleConfigPanel (same field order: min hour dom
 * month dow). The monthly preset runs on day 1 (the suggestion never carries a
 * day-of-month, so we anchor it deterministically).
 */
function buildCronExpression(
  preset: CronPreset,
  time: string,
  dayOfWeek: number,
): string {
  const [hours, minutes] = time.split(':').map((n) => parseInt(n, 10) || 0);
  switch (preset) {
    case 'daily':
      return `${minutes} ${hours} * * *`;
    case 'weekdays':
      return `${minutes} ${hours} * * 1-5`;
    case 'weekly':
      return `${minutes} ${hours} * * ${dayOfWeek}`;
    case 'monthly':
      return `${minutes} ${hours} 1 * *`;
    case 'hourly':
      return `${minutes} * * * *`;
  }
}

/** A common, browser-default-first timezone list (mirrors ScheduleConfigPanel). */
function getTimezoneOptions(browserTz: string): string[] {
  let allZones: string[];
  try {
    allZones = (Intl as any).supportedValuesOf('timeZone');
  } catch {
    allZones = [
      'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
      'America/Los_Angeles', 'Europe/London', 'Europe/Berlin', 'Europe/Paris',
      'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata', 'Australia/Sydney',
    ];
  }
  const sorted = allZones.filter((z) => z !== browserTz);
  sorted.unshift(browserTz);
  return sorted;
}

/** Resolve the browser's IANA timezone, defaulting to UTC if unavailable. */
function resolveBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Seed the card's form fields from the backend's suggested cadence. */
function seedFromSuggestion(suggested: SuggestedSchedule | undefined): {
  scheduleType: ScheduleType;
  cronPreset: CronPreset;
  cronTime: string;
  dayOfWeek: number;
  intervalMinutes: string;
} {
  const base = {
    scheduleType: 'cron' as ScheduleType,
    cronPreset: 'daily' as CronPreset,
    cronTime: '08:00',
    dayOfWeek: 1,
    intervalMinutes: '60',
  };
  if (!suggested) return base;

  if (suggested.schedule_type === 'interval') {
    return {
      ...base,
      scheduleType: 'interval',
      intervalMinutes: String(suggested.interval_minutes ?? 60),
    };
  }
  return {
    ...base,
    scheduleType: 'cron',
    cronPreset: (suggested.cron_preset as CronPreset) ?? 'daily',
    cronTime: suggested.cron_time ?? '08:00',
    dayOfWeek: suggested.day_of_week ?? 1,
  };
}

/**
 * ScheduleConfirmCard (AC-1790, epic AC-1787). The FINAL concierge step: when a
 * user's email-summary goal implies recurrence ("summarize my emails every
 * morning"), the just-built workflow can be put on a recurring schedule WITHOUT
 * leaving chat. The backend parses a SUGGESTED cadence from the goal phrasing;
 * this card prefills from it and lets the user adjust the preset / time / timezone
 * before confirming.
 *
 * Built as a sibling of McpConfigureBuiltinCard (same accessible role="group"
 * region, text-not-color status, >=44px tap targets). The cron-preset / time /
 * timezone controls reuse ScheduleConfigPanel's vocabulary (daily/weekdays/
 * weekly/monthly/hourly + buildCronExpression). The timezone defaults to the
 * browser's IANA zone. On Confirm the card emits the FINAL cadence — for cron,
 * a built `cron_expression`; for interval, `interval_minutes` — plus the
 * timezone and `is_enabled: true`. The backend takes the orchestration_id from
 * the stored action_data (authoritative); the field on the request is for parity
 * only.
 *
 * State drives affordances:
 *   - 'confirming' : Confirm/Decline disabled + aria-busy on the container.
 *   - 'confirmed'  : a "Schedule created" indicator replaces the actions.
 *   - 'declined'   : a dimmed "Declined" note.
 *   - 'superseded' : Confirm disabled (a newer turn replaced this card).
 */
const ScheduleConfirmCard: React.FC<ScheduleConfirmCardProps> = ({
  preview,
  state,
  onConfirm,
  onDecline,
}) => {
  const headingId = useId();
  const messageId = useId();
  const presetId = useId();
  const timeId = useId();
  const dayId = useId();
  const intervalId = useId();
  const timezoneId = useId();

  const seed = useMemo(
    () => seedFromSuggestion(preview.suggested_schedule),
    [preview.suggested_schedule],
  );

  const [scheduleType, setScheduleType] = useState<ScheduleType>(seed.scheduleType);
  const [cronPreset, setCronPreset] = useState<CronPreset>(seed.cronPreset);
  const [cronTime, setCronTime] = useState(seed.cronTime);
  const [dayOfWeek, setDayOfWeek] = useState(seed.dayOfWeek);
  const [intervalMinutes, setIntervalMinutes] = useState(seed.intervalMinutes);
  const [timezone, setTimezone] = useState(resolveBrowserTimezone());

  const timezoneOptions = useMemo(
    () => getTimezoneOptions(resolveBrowserTimezone()),
    [],
  );

  const isConfirming = state === 'confirming';
  const isConfirmed = state === 'confirmed';
  const isDeclined = state === 'declined';
  const isSuperseded = state === 'superseded';
  const isLocked = isConfirming || isConfirmed || isDeclined;

  const showTime = ['daily', 'weekdays', 'weekly', 'monthly'].includes(cronPreset);

  const handleConfirm = () => {
    if (scheduleType === 'interval') {
      const minutes = parseInt(intervalMinutes, 10);
      onConfirm({
        orchestration_id: preview.orchestration_id,
        schedule_type: 'interval',
        interval_minutes: Number.isFinite(minutes) && minutes > 0 ? minutes : 60,
        timezone,
        is_enabled: true,
      });
      return;
    }
    onConfirm({
      orchestration_id: preview.orchestration_id,
      schedule_type: 'cron',
      cron_expression: buildCronExpression(cronPreset, cronTime, dayOfWeek),
      timezone,
      is_enabled: true,
    });
  };

  return (
    <div
      role="group"
      aria-labelledby={headingId}
      aria-describedby={messageId}
      aria-live="polite"
      aria-busy={isConfirming ? 'true' : undefined}
      className={`mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-3 focus:outline-none ${
        isDeclined ? 'opacity-50' : ''
      }`}
    >
      <div>
        {/* Generic heading; the workflow being scheduled is named in the message
            below (mirrors McpConfigureBuiltinCard's id-free message pattern). */}
        <h4
          id={headingId}
          className="text-sm font-semibold text-gray-900 dark:text-warm-white"
        >
          Set up the schedule
        </h4>
        {!isConfirmed && (
          <p
            id={messageId}
            className="text-xs text-gray-600 dark:text-gray-400 mt-0.5"
          >
            {preview.message}
          </p>
        )}
      </div>

      {/* Cadence form. Reuses ScheduleConfigPanel's preset / time / timezone
          vocabulary, prefilled from the backend's suggestion. Native controls
          (select / input) carry accessible names via the visible labels. */}
      {!isConfirmed && !isDeclined && (
        <div className="flex flex-col gap-3">
          {/* Frequency preset (cron) — hidden when the suggestion is an interval. */}
          {scheduleType === 'cron' && (
            <div className="flex flex-col gap-1">
              <label
                htmlFor={presetId}
                className="text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                Frequency
              </label>
              <select
                id={presetId}
                value={cronPreset}
                onChange={(e) => setCronPreset(e.target.value as CronPreset)}
                disabled={isLocked || isSuperseded}
                className="min-h-[44px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
              >
                {CRON_PRESET_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Time (for daily / weekdays / weekly / monthly). */}
          {scheduleType === 'cron' && showTime && (
            <div className="flex flex-col gap-1">
              <label
                htmlFor={timeId}
                className="text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                Time
              </label>
              <input
                id={timeId}
                type="time"
                value={cronTime}
                onChange={(e) => setCronTime(e.target.value)}
                disabled={isLocked || isSuperseded}
                className="min-h-[44px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
              />
            </div>
          )}

          {/* Day of week (weekly only). */}
          {scheduleType === 'cron' && cronPreset === 'weekly' && (
            <div className="flex flex-col gap-1">
              <label
                htmlFor={dayId}
                className="text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                Day of week
              </label>
              <select
                id={dayId}
                value={String(dayOfWeek)}
                onChange={(e) => setDayOfWeek(parseInt(e.target.value, 10))}
                disabled={isLocked || isSuperseded}
                className="min-h-[44px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
              >
                {DAY_OF_WEEK_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Interval minutes (interval suggestion only). */}
          {scheduleType === 'interval' && (
            <div className="flex flex-col gap-1">
              <label
                htmlFor={intervalId}
                className="text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                Run every (minutes)
              </label>
              <input
                id={intervalId}
                type="number"
                min={1}
                value={intervalMinutes}
                onChange={(e) => setIntervalMinutes(e.target.value)}
                disabled={isLocked || isSuperseded}
                className="min-h-[44px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
              />
            </div>
          )}

          {/* Timezone (defaults to the browser's IANA zone). */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor={timezoneId}
              className="text-xs font-medium text-gray-700 dark:text-gray-300"
            >
              Timezone
            </label>
            <select
              id={timezoneId}
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={isLocked || isSuperseded}
              className="min-h-[44px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
            >
              {timezoneOptions.map((z) => (
                <option key={z} value={z}>
                  {z.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Actions / status (text, not color-only). */}
      {isConfirmed ? (
        <div className="text-xs font-medium text-green-700 dark:text-green-400">
          Schedule created for {preview.workflow_name}
        </div>
      ) : isDeclined ? (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Declined — nothing was scheduled
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isConfirming || isSuperseded}
            className="w-full min-h-[44px] text-sm font-medium bg-blue-500 text-white px-3 py-2 rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Set up schedule
          </button>
          <button
            type="button"
            onClick={onDecline}
            disabled={isConfirming}
            className="w-full min-h-[44px] text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Not now
          </button>
        </div>
      )}
    </div>
  );
};

export default ScheduleConfirmCard;
